<template>
    <div>
        <ts-page-title
            title="Loan Drop Out"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: 'Loan Drop Out', active: true }
            ]"
        />

        <!-- ────────────────── FILTER PANEL ────────────────── -->
        <ts-panel>
            <ts-panel-wrapper class="tw-py-3">
                <div
                    class="tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-select-none"
                    @click="filtersOpen = !filtersOpen"
                >
                    <div class="tw-flex tw-items-center tw-space-x-3 tw-flex-1 tw-min-w-0">
                        <i
                            class="fas tw-text-gray-400"
                            :class="filtersOpen ? 'fa-chevron-down' : 'fa-chevron-right'"
                            style="width: 12px"
                        ></i>
                        <b class="tw-text-gray-700 tw-whitespace-nowrap">Filter Criteria</b>
                        <div v-if="activeChips.length" class="tw-flex tw-flex-wrap tw-gap-1 tw-flex-1 tw-min-w-0">
                            <span
                                v-for="chip in activeChips"
                                :key="chip.key"
                                class="chip-pill"
                                :class="chip.tone || 'chip-default'"
                            >
                                <i v-if="chip.icon" :class="'fas ' + chip.icon + ' tw-mr-1'"></i>
                                {{ chip.label }}
                            </span>
                        </div>
                    </div>
                    <span class="tw-text-sm tw-text-blue-600 tw-whitespace-nowrap tw-ml-3">
                        {{ filtersOpen ? 'Collapse' : 'Edit filters' }}
                    </span>
                </div>

                <div v-show="filtersOpen" class="tw-mt-3">
                    <!-- Row 1: Mode / Branch / CO / Currency -->
                    <div class="filter-grid">
                        <div class="filter-cell" style="grid-column: span 3">
                            <label class="filter-lbl">Mode</label>
                            <div class="pill-group">
                                <button
                                    v-for="m in modeOptions"
                                    :key="m.value"
                                    type="button"
                                    class="pill"
                                    :class="{ 'pill-active': model.mode === m.value }"
                                    @click="model.mode = m.value"
                                    :title="m.hint"
                                >
                                    <i :class="'fas ' + m.icon + ' tw-mr-1'"></i>
                                    {{ m.label }}
                                </button>
                            </div>
                        </div>

                        <div class="filter-cell" style="grid-column: span 3">
                            <label class="filter-lbl">Branch <span class="tw-text-gray-400 tw-font-normal tw-normal-case">(all if empty)</span></label>
                            <ts-branch-filter
                                @filter="value => (model.branch_id = value)"
                                :isPlaceholder="true"
                            />
                        </div>

                        <div class="filter-cell" style="grid-column: span 4">
                            <label class="filter-lbl">Credit Officer</label>
                            <Select
                                v-model="model.co_id"
                                multiple
                                :max-tag-count="1"
                                :filterable="true"
                                :filter-by-label="true"
                                placeholder="All COs"
                            >
                                <Option
                                    v-for="item in employees"
                                    :value="item.employee_id"
                                    :key="item.employee_id"
                                    :label="item.employee_name_en"
                                >
                                    {{ item.employee_name_en }}
                                </Option>
                            </Select>
                        </div>

                        <div class="filter-cell" style="grid-column: span 2">
                            <label class="filter-lbl">Currency <span class="text-danger">*</span></label>
                            <div class="pill-group">
                                <button
                                    v-for="c in currencies"
                                    :key="c.currency_id"
                                    type="button"
                                    class="pill"
                                    :class="{ 'pill-active': model.currency_id === c.currency_id }"
                                    @click="model.currency_id = c.currency_id"
                                >{{ c.currency_code }}</button>
                            </div>
                        </div>
                    </div>

                    <!-- Row 2 — WINDOW mode: date range + N days -->
                    <div v-if="isWindow" class="filter-grid tw-mt-3 tw-items-end">
                        <div
                            class="filter-cell"
                            :style="{ 'grid-column': model.date_preset === 'custom' ? 'span 7' : 'span 4' }"
                        >
                            <label class="filter-lbl">Finished Between <span class="text-danger">*</span></label>
                            <div class="date-row">
                                <div class="pill-group">
                                    <button
                                        v-for="p in datePresets"
                                        :key="p.value"
                                        type="button"
                                        class="pill"
                                        :class="{ 'pill-active': model.date_preset === p.value }"
                                        @click="model.date_preset = p.value"
                                    >{{ p.label }}</button>
                                </div>
                                <template v-if="model.date_preset === 'custom'">
                                    <div class="date-picker-wrap">
                                        <DatePicker
                                            type="date" format="dd-MM-yyyy"
                                            placeholder="From" style="width: 100%"
                                            :options="datePickerOptions"
                                            @on-change="val => (model.date_from = val || null)"
                                        ></DatePicker>
                                    </div>
                                    <div class="date-picker-wrap">
                                        <DatePicker
                                            type="date" format="dd-MM-yyyy"
                                            placeholder="To" style="width: 100%"
                                            :options="datePickerOptions"
                                            @on-change="val => (model.date_to = val || null)"
                                        ></DatePicker>
                                    </div>
                                </template>
                                <div v-else class="date-preview">{{ presetPreview }}</div>
                            </div>
                        </div>

                        <div class="filter-cell" style="grid-column: span 2">
                            <label class="filter-lbl">No Redisb Within</label>
                            <InputNumber
                                v-model="model.num_days"
                                :min="1" :max="365"
                                style="width: 100%"
                            ></InputNumber>
                        </div>

                        <div
                            class="filter-cell"
                            :style="{ 'grid-column': model.date_preset === 'custom' ? 'span 3' : 'span 6' }"
                        >
                            <label class="filter-lbl">Search</label>
                            <div class="search-group">
                                <div class="search-input-wrap">
                                    <input
                                        type="text"
                                        class="form-control search-input"
                                        v-model="searchQuery"
                                        placeholder="Loan #, customer code/name, CO name"
                                        @keyup.enter="onSearch"
                                    />
                                    <button v-if="searchQuery" type="button" class="search-clear"
                                        title="Clear search" @click.prevent="onClearSearch">
                                        <i class="fas fa-times-circle"></i>
                                    </button>
                                </div>
                                <button type="button" class="search-btn" :disabled="loading" @click.prevent="onSearch">
                                    <i class="fas fa-circle-notch fa-spin" v-if="loading"></i>
                                    <i class="fas fa-search" v-else></i>
                                    <span class="tw-ml-1">Search</span>
                                </button>
                                <button type="button" class="reset-btn" @click.prevent="onReset" title="Reset all filters">
                                    <i class="fas fa-undo"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Row 2 — SNAPSHOT mode: as-of date + status + search -->
                    <div v-else class="filter-grid tw-mt-3 tw-items-end">
                        <div class="filter-cell" style="grid-column: span 3">
                            <label class="filter-lbl">As of Date <span class="text-danger">*</span></label>
                            <div class="date-row">
                                <DatePicker
                                    v-model="model.as_of_date"
                                    type="date" format="dd-MM-yyyy"
                                    placeholder="Select date (up to yesterday)"
                                    style="width: 100%"
                                    :options="snapshotDatePickerOptions"
                                    @on-change="val => (model.as_of_date = val || null)"
                                ></DatePicker>
                            </div>
                        </div>

                        <div class="filter-cell" style="grid-column: span 3">
                            <label class="filter-lbl">Status</label>
                            <div class="pill-group">
                                <button type="button" class="pill"
                                    :class="{ 'pill-active': model.status === 'all' }"
                                    @click="model.status = 'all'">All</button>
                                <button type="button" class="pill"
                                    :class="{ 'pill-active': model.status === 'inactive' }"
                                    @click="model.status = 'inactive'">Inactive</button>
                                <button type="button" class="pill"
                                    :class="{ 'pill-active': model.status === 'active' }"
                                    @click="model.status = 'active'">Active</button>
                            </div>
                        </div>

                        <div class="filter-cell" style="grid-column: span 6">
                            <label class="filter-lbl">Search</label>
                            <div class="search-group">
                                <div class="search-input-wrap">
                                    <input
                                        type="text"
                                        class="form-control search-input"
                                        v-model="searchQuery"
                                        placeholder="Loan #, customer code/name, CO name"
                                        @keyup.enter="onSearch"
                                    />
                                    <button v-if="searchQuery" type="button" class="search-clear"
                                        title="Clear search" @click.prevent="onClearSearch">
                                        <i class="fas fa-times-circle"></i>
                                    </button>
                                </div>
                                <button type="button" class="search-btn" :disabled="loading" @click.prevent="onSearch">
                                    <i class="fas fa-circle-notch fa-spin" v-if="loading"></i>
                                    <i class="fas fa-search" v-else></i>
                                    <span class="tw-ml-1">Search</span>
                                </button>
                                <button type="button" class="reset-btn" @click.prevent="onReset" title="Reset all filters">
                                    <i class="fas fa-undo"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </ts-panel-wrapper>
        </ts-panel>

        <!-- ────────────────── SUMMARY CARDS ────────────────── -->
        <div class="summary-grid tw-mt-3">
            <div
                v-for="card in summaryCards"
                :key="card.key"
                class="summary-card"
                :class="'summary-card--' + card.tone"
            >
                <div class="summary-card__header">
                    <div class="summary-card__icon"><i :class="'fas ' + card.icon"></i></div>
                    <div class="summary-card__label">{{ card.label }}</div>
                    <div v-if="card.badge !== undefined" class="summary-card__count">
                        <template v-if="summaryLoading">…</template>
                        <template v-else>{{ card.badge }}</template>
                    </div>
                </div>
                <div class="summary-card__value">
                    <template v-if="summaryLoading">
                        <span class="tw-text-gray-300">—</span>
                    </template>
                    <template v-else>
                        <span class="summary-card__amount">{{ card.formatted }}</span>
                        <span class="summary-card__currency">{{ card.unit }}</span>
                    </template>
                </div>
            </div>
        </div>

        <!-- ────────────────── RESULTS CARD ────────────────── -->
        <div class="results-card tw-mt-3">
            <div class="results-toolbar">
                <div class="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-flex-wrap">
                    <template v-if="loading">
                        <span class="tw-text-gray-500">
                            <i class="fas fa-circle-notch fa-spin tw-mr-1"></i> Loading…
                        </span>
                    </template>
                    <template v-else-if="meta.total === null">
                        <span class="tw-text-gray-400">
                            <i class="fas fa-info-circle tw-mr-1"></i> No search yet
                        </span>
                    </template>
                    <template v-else-if="meta.total === 0">
                        <span class="tw-text-gray-500">
                            <i class="far fa-folder-open tw-mr-1"></i> No results
                        </span>
                    </template>
                    <template v-else>
                        <span class="tw-font-bold tw-text-gray-800 tw-text-base">
                            {{ meta.total.toLocaleString() }} rows
                        </span>
                        <span class="tw-text-gray-500">
                            · Page {{ meta.current_page }} of {{ meta.last_page }}
                        </span>
                    </template>
                </div>

                <div class="export-group" title="Export the current filters to xlsx">
                    <select v-model="model.excel_template" class="export-select">
                        <option value="standard">Standard xlsx</option>
                        <option value="raw-data">Raw Data xlsx</option>
                    </select>
                    <button
                        type="button"
                        class="export-btn"
                        :disabled="exporting"
                        @click.prevent="exportExcel"
                    >
                        <i class="fas fa-circle-notch fa-spin" v-if="exporting"></i>
                        <i class="far fa-file-excel" v-else></i>
                        <span class="tw-ml-1">Export</span>
                    </button>
                </div>
            </div>

            <div
                class="results-body tw-relative"
                :style="{ minHeight: hasRows ? '0px' : emptyBodyHeight + 'px' }"
            >
                <Spin size="large" fix v-if="loading"></Spin>

                <div v-if="!loading && meta.total === null" class="empty-state">
                    <i class="fas fa-search tw-text-2xl tw-mb-2 tw-text-gray-300"></i>
                    <div class="tw-text-sm tw-text-gray-500">Ready when you are — click <b>Search</b> above.</div>
                </div>

                <div v-else-if="!loading && meta.total === 0" class="empty-state">
                    <i class="far fa-folder-open tw-text-2xl tw-mb-2 tw-text-gray-300"></i>
                    <div class="tw-text-sm tw-text-gray-500">No drop-outs for the selected filters.</div>
                </div>

                <Table
                    v-else
                    :columns="columns"
                    :data="rows"
                    border
                    stripe
                    size="small"
                    :height="tableHeight"
                    show-summary
                    :summary-method="summaryMethod"
                    @on-sort-change="onSortChange"
                ></Table>
            </div>

            <div v-if="meta.total > 0" class="results-pagination">
                <div class="pagination-left">
                    <span class="pagination-lbl">Per page:</span>
                    <Select
                        v-model="perPage"
                        @on-change="onPerPageChange"
                        style="width: 80px"
                        size="small"
                    >
                        <Option :value="10">10</Option>
                        <Option :value="30">30</Option>
                        <Option :value="50">50</Option>
                        <Option :value="100">100</Option>
                    </Select>
                    <span class="pagination-divider">|</span>
                    <span class="pagination-lbl">
                        Showing <b>{{ rangeStart.toLocaleString() }}</b>–<b>{{ rangeEnd.toLocaleString() }}</b>
                        of <b>{{ meta.total.toLocaleString() }}</b>
                    </span>
                </div>
                <Page
                    :current="meta.current_page"
                    :total="meta.total || 0"
                    :page-size="perPage"
                    @on-change="onPageChange"
                    size="small"
                    show-elevator
                ></Page>
            </div>
        </div>
    </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import { debounce, trim } from 'lodash'
import moment from 'moment'
import * as FileDownload from 'downloadjs'

export default {
    name: 'loanDropOutV3',
    data () {
        return {
            filtersOpen: true,
            loading: false,
            exporting: false,
            errors: new Errors(),
            searchQuery: '',
            perPage: 30,
            viewportHeight: window.innerHeight,
            summaryLoading: false,
            summary: {
                inactive:  { count: 0, amount: '0' },
                active:    { count: 0 },
                customers: { count: 0 },
                cos:       { count: 0 }
            },
            totals: {
                loan_amount: '0'
            },
            rows: [],
            meta: { current_page: 1, last_page: 1, total: null },
            modeOptions: [
                { value: 'window',   label: 'Window',   icon: 'fa-calendar-week',
                  hint: 'Loans finished in a range with no new disbursement within N days' },
                { value: 'snapshot', label: 'Snapshot', icon: 'fa-camera',
                  hint: 'Customers whose latest loan has no future scheduled cycle' }
            ],
            datePresets: [
                { value: 'yesterday',  label: 'Yesterday' },
                { value: 'this_month', label: 'This Month' },
                { value: 'last_month', label: 'Last Month' },
                { value: 'custom',     label: 'Custom' }
            ],
            model: {
                mode: 'window',
                branch_id: [],
                co_id: [],
                currency_id: null,
                date_preset: 'this_month',
                date_from: null,
                date_to: null,
                num_days: 30,
                as_of_date: null,
                status: 'all',
                search: null,
                sort: 'finished_date',
                sort_dir: 'desc',
                page: 1,
                perPage: 30,
                excel_template: 'standard'
            }
        }
    },
    created () {
        this.model.currency_id = this.$store.state.authUser.sys_setting[0].default_currency_id
    },
    computed: {
        ...mapState('report/resource', ['employees', 'currencies']),

        currencyCode () {
            if (!this.model.currency_id) return ''
            const c = (this.currencies || []).find(c => c.currency_id === this.model.currency_id)
            return c ? c.currency_code : ''
        },
        isKh () {
            const locale = (this.$i18n && this.$i18n.locale) || localStorage.getItem('langCode')
            return locale === 'kh'
        },
        isWindow () {
            return this.model.mode === 'window'
        },

        presetPreview () {
            const today = moment()
            switch (this.model.date_preset) {
                case 'yesterday':  return today.clone().subtract(1, 'day').format('ddd, DD MMM YYYY')
                case 'this_month': return today.clone().startOf('month').format('DD MMM')
                    + ' – ' + today.clone().endOf('month').format('DD MMM YYYY')
                case 'last_month': {
                    const lm = today.clone().subtract(1, 'month')
                    return lm.clone().startOf('month').format('DD MMM')
                        + ' – ' + lm.clone().endOf('month').format('DD MMM YYYY')
                }
                case 'custom':
                    return (this.model.date_from && this.model.date_to)
                        ? this.model.date_from + ' – ' + this.model.date_to
                        : null
            }
            return ''
        },

        datePickerOptions () {
            // Window mode's From/To may be today (finished today still counts).
            return {
                disabledDate (date) {
                    return date && date.valueOf() > moment().endOf('day').valueOf()
                }
            }
        },
        snapshotDatePickerOptions () {
            // Snapshot mode: today's data isn't final. Backend rejects today
            // and future — keep the picker in sync.
            return {
                disabledDate (date) {
                    return date && date.valueOf() >= moment().startOf('day').valueOf()
                }
            }
        },

        activeChips () {
            const chips = []
            chips.push({
                key: 'mode',
                icon: this.isWindow ? 'fa-calendar-week' : 'fa-camera',
                label: this.isWindow ? 'Window' : 'Snapshot',
                tone: 'chip-type'
            })
            if (this.model.branch_id && this.model.branch_id.length) {
                chips.push({ key: 'branch', icon: 'fa-building',
                    label: this.model.branch_id.length + ' branch' + (this.model.branch_id.length > 1 ? 'es' : ''),
                    tone: 'chip-branch' })
            } else {
                chips.push({ key: 'branch', icon: 'fa-building', label: 'All branches', tone: 'chip-branch' })
            }
            if (this.model.currency_id) {
                const cur = this.currencies.find(c => c.currency_id === this.model.currency_id)
                if (cur) chips.push({ key: 'currency', label: cur.currency_code, tone: 'chip-currency' })
            }
            if (this.isWindow && this.presetPreview) {
                chips.push({ key: 'date', icon: 'fa-calendar-alt', label: this.presetPreview, tone: 'chip-date' })
                chips.push({ key: 'days', icon: 'fa-stopwatch', label: this.model.num_days + 'd gap', tone: 'chip-search' })
            }
            if (!this.isWindow && this.model.as_of_date) {
                chips.push({ key: 'as_of', icon: 'fa-calendar-alt', label: 'As of ' + this.model.as_of_date, tone: 'chip-date' })
            }
            if (!this.isWindow && this.model.status !== 'all') {
                chips.push({ key: 'status', icon: 'fa-filter',
                    label: this.model.status === 'inactive' ? 'Inactive only' : 'Active only',
                    tone: 'chip-type' })
            }
            if (this.model.co_id && this.model.co_id.length) {
                chips.push({ key: 'co', icon: 'fa-user',
                    label: this.model.co_id.length + ' CO' + (this.model.co_id.length > 1 ? 's' : ''),
                    tone: 'chip-co' })
            }
            if (this.searchQuery && this.searchQuery.trim().length >= 2) {
                chips.push({ key: 'search', icon: 'fa-search', label: '"' + this.searchQuery.trim() + '"', tone: 'chip-search' })
            }
            return chips.filter(c => c.label)
        },

        tableHeight () {
            const chrome = this.filtersOpen ? 601 : 471
            const h = this.viewportHeight - chrome
            return Math.max(280, h)
        },
        emptyBodyHeight () {
            const PAGINATION_ROW = 52
            return this.tableHeight + PAGINATION_ROW
        },
        hasRows () {
            return !this.loading && this.meta.total > 0
        },
        rangeStart () {
            if (!this.meta.total) return 0
            return (this.meta.current_page - 1) * this.perPage + 1
        },
        rangeEnd () {
            if (!this.meta.total) return 0
            return Math.min(this.meta.current_page * this.perPage, this.meta.total)
        },

        summaryCards () {
            const s = this.summary
            return [
                {
                    key: 'inactive', tone: 'past_due', icon: 'fa-user-slash',
                    label: 'Drop-outs (Inactive)',
                    badge: (s.inactive.count || 0).toLocaleString(),
                    formatted: this.fmt(s.inactive.amount),
                    unit: this.currencyCode
                },
                {
                    key: 'active', tone: 'cur_paid', icon: 'fa-user-check',
                    label: 'Still Active',
                    badge: (s.active.count || 0).toLocaleString(),
                    formatted: (s.active.count || 0).toLocaleString(),
                    unit: 'loans'
                },
                {
                    key: 'cust', tone: 'cur_due', icon: 'fa-users',
                    label: 'Customers',
                    formatted: (s.customers.count || 0).toLocaleString(),
                    unit: 'unique'
                },
                {
                    key: 'cos', tone: 'early', icon: 'fa-user-tie',
                    label: 'Credit Officers',
                    formatted: (s.cos.count || 0).toLocaleString(),
                    unit: 'unique'
                }
            ]
        },

        columns () {
            return [
                {
                    title: 'Branch', key: 'branch_code', width: 90, fixed: 'left', align: 'center', sortable: 'custom',
                    render: (h, params) => h('span', { class: 'branch-code' }, params.row.branch_code || '—')
                },
                {
                    title: 'Loan Account #', key: 'loan_account_no', width: 150, fixed: 'left', sortable: 'custom',
                    render: (h, params) => h('code', { class: 'tw-text-xs tw-text-gray-700' }, params.row.loan_account_no || '—')
                },
                { title: 'Customer Code', key: 'customer_code', width: 130, sortable: 'custom',
                  render: (h, params) => h('code', { class: 'tw-text-xs tw-text-gray-700' }, params.row.customer_code || '—') },
                {
                    title: 'Customer Name', key: 'customer_name_en', minWidth: 180, sortable: 'custom',
                    render: (h, params) => h('span', { class: 'name-text' }, this.isKh
                        ? (params.row.customer_name_kh || params.row.customer_name_en || '—')
                        : (params.row.customer_name_en || params.row.customer_name_kh || '—'))
                },
                { title: 'Phone', key: 'contact_no', width: 120,
                  render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-700' }, params.row.contact_no || '—') },
                { title: 'Village', key: 'village_en', width: 130,
                  render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-700' },
                      (this.isKh ? params.row.village_kh : params.row.village_en) || '—') },
                { title: 'Loan Product', key: 'loan_product_en', width: 140,
                  render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-700' },
                      (this.isKh ? params.row.loan_product_kh : params.row.loan_product_en) || '—') },
                { title: 'Disbursed', key: 'disbursement_date', width: 110, align: 'center', sortable: 'custom',
                  render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-600' },
                      params.row.disbursement_date ? moment(params.row.disbursement_date).format('DD-MMM-YY') : '—') },
                { title: 'Finished', key: 'finished_date', width: 110, align: 'center', sortable: 'custom',
                  render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-600' },
                      params.row.finished_date ? moment(params.row.finished_date).format('DD-MMM-YY') : '—') },
                {
                    title: 'CO Name', key: 'co_name_en', minWidth: 160, sortable: 'custom',
                    render: (h, params) => h('span', { class: 'name-text' }, this.isKh
                        ? (params.row.co_name_kh || params.row.co_name_en || '—')
                        : (params.row.co_name_en || params.row.co_name_kh || '—'))
                },
                { title: 'Status', key: 'status', width: 100, align: 'center', sortable: 'custom',
                  render: (h, params) => this.statusCell(params.row.status) },
                { title: 'Loan Amount', key: 'loan_amount', width: 150, align: 'right', sortable: 'custom',
                  render: (h, params) => this.moneyCell(params.row.loan_amount) }
            ]
        }
    },
    methods: {
        ...mapActions('report/resource', ['getEmployee', 'getCurrency']),

        fmt (v) {
            const n = parseFloat(v)
            if (isNaN(n) || n === 0) return '—'
            return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        },
        fmtStrict (v) {
            const n = parseFloat(v)
            if (isNaN(n)) return '0'
            return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        },
        moneyCell (val) {
            const h = this.$createElement
            const n = parseFloat(val)
            if (isNaN(n) || n === 0) return h('span', { class: 'tw-text-gray-300' }, '—')
            return h('span', this.fmt(val))
        },
        statusCell (val) {
            const h = this.$createElement
            const s = (val || '').toString()
            const cls = s === 'Inactive' ? 'status-pill status-inactive'
                     : s === 'Active'   ? 'status-pill status-active'
                     : 'tw-text-gray-300'
            if (!s) return h('span', { class: 'tw-text-gray-300' }, '—')
            return h('span', { class: cls }, s)
        },

        summaryMethod ({ columns }) {
            const t = this.totals
            const sums = {}
            columns.forEach((col, idx) => {
                if (idx === 0) { sums[col.key] = { key: col.key, value: 'TOTAL' }; return }
                let value = ''
                if (col.key === 'loan_amount') value = this.fmtStrict(t.loan_amount)
                sums[col.key] = { key: col.key, value }
            })
            return sums
        },

        buildParams () {
            const p = {
                mode:           this.model.mode,
                branch_id:      this.model.branch_id,
                currency_id:    this.model.currency_id,
                co_id:          this.model.co_id,
                sort:           this.model.sort,
                sort_dir:       this.model.sort_dir,
                page:           this.model.page,
                perPage:        this.perPage,
                excel_template: this.model.excel_template
            }
            if (this.isWindow) {
                p.date_preset = this.model.date_preset
                p.num_days    = this.model.num_days
                if (this.model.date_preset === 'custom') {
                    p.date_from = this.model.date_from
                    p.date_to   = this.model.date_to
                }
            } else {
                p.as_of_date = this.model.as_of_date
                p.status     = this.model.status
            }
            if (this.searchQuery && this.searchQuery.trim().length >= 2) {
                p.search = this.searchQuery.trim()
            }
            return p
        },

        fetchData () {
            this.errors = new Errors()
            this.loading = true
            this.summaryLoading = true

            this.$store
                .dispatch('report/creditOperation/loanDropOutV3', this.buildParams())
                .then(response => {
                    const data    = response.data || []
                    const meta    = response.meta || response
                    const summary = response.summary || null
                    const totals  = response.totals  || null

                    this.rows = data
                    this.meta = {
                        current_page: meta.current_page || 1,
                        last_page:    meta.last_page    || 1,
                        total:        meta.total ?? 0
                    }

                    if (summary) {
                        this.summary = {
                            inactive:  Object.assign({ count: 0, amount: '0' }, summary.inactive  || {}),
                            active:    Object.assign({ count: 0 },               summary.active    || {}),
                            customers: Object.assign({ count: 0 },               summary.customers || {}),
                            cos:       Object.assign({ count: 0 },               summary.cos       || {})
                        }
                    }
                    if (totals) {
                        this.totals = Object.assign({}, this.totals, totals)
                    }
                })
                .catch(error => {
                    this.notice({ type: 'error', text: (error && error.message) || 'Failed to load report' })
                    this.errors = new Errors((error && error.errors) || {})
                    this.rows = []
                })
                .finally(() => {
                    this.loading = false
                    this.summaryLoading = false
                })
        },

        onSearch () {
            if (this.isWindow && this.model.date_preset === 'custom'
                && (!this.model.date_from || !this.model.date_to)) {
                this.notice({ type: 'warning', text: 'Please pick both From and To dates.' })
                return
            }
            if (!this.isWindow && !this.model.as_of_date) {
                this.notice({ type: 'warning', text: 'Please pick an As-of date.' })
                return
            }
            this.model.page = 1
            this.filtersOpen = false
            this.fetchData()
        },

        onClearSearch () {
            const hadSearch = !!(this.searchQuery && this.searchQuery.trim())
            this.searchQuery = ''
            if (hadSearch && this.meta.total !== null) {
                this.model.page = 1
                this.fetchData()
            }
        },

        onReset () {
            this.searchQuery = ''
            this.model.co_id = []
            this.model.mode = 'window'
            this.model.date_preset = 'this_month'
            this.model.date_from = null
            this.model.date_to = null
            this.model.num_days = 30
            this.model.as_of_date = null
            this.model.status = 'all'
            this.model.sort = 'finished_date'
            this.model.sort_dir = 'desc'
            this.model.page = 1
            this.rows = []
            this.meta = { current_page: 1, last_page: 1, total: null }
        },

        onSortChange ({ key, order }) {
            if (order === 'normal') {
                this.model.sort = 'finished_date'
                this.model.sort_dir = 'desc'
            } else {
                this.model.sort = key
                this.model.sort_dir = order === 'asc' ? 'asc' : 'desc'
            }
            this.model.page = 1
            this.fetchData()
        },

        onPageChange (page) {
            this.model.page = page
            this.fetchData()
        },
        onPerPageChange (v) {
            this.perPage = v
            this.model.page = 1
            this.fetchData()
        },

        exportExcel () {
            this.errors = new Errors()
            this.exporting = true

            this.$store
                .dispatch('report/creditOperation/loanDropOutV3ExportExcel', this.buildParams())
                .then(response => {
                    const filename = trim(
                        response.headers['content-disposition']
                            .substring(response.headers['content-disposition'].indexOf('filename'))
                            .replace('filename=', ''),
                        '"'
                    )
                    FileDownload(response.data, filename)
                })
                .catch(err => {
                    let msg = 'Export failed'
                    try {
                        const parsed = JSON.parse(String.fromCharCode.apply(null, new Uint8Array(err)))
                        msg = parsed.message || msg
                        this.errors = new Errors(parsed.errors || {})
                    } catch (e) { /* not JSON */ }
                    this.notice({ type: 'error', text: msg })
                })
                .finally(() => { this.exporting = false })
        },

        notice (not) {
            this.$Notice[not.type]({
                title: 'LOAN DROP OUT',
                desc: not.text
            })
        }
    },
    watch: {
        'model.branch_id': debounce(function (value) {
            this.model.co_id = []
            this.getEmployee({ branch_id: value })
        }, 500),
        'model.mode' () {
            // Reset mode-specific fields when switching so Search doesn't see stale state.
            this.rows = []
            this.meta = { current_page: 1, last_page: 1, total: null }
            this.model.page = 1
        }
    },
    mounted () {
        this._onResize = debounce(() => { this.viewportHeight = window.innerHeight }, 100)
        window.addEventListener('resize', this._onResize)
    },
    beforeDestroy () {
        if (this._onResize) window.removeEventListener('resize', this._onResize)
    },
    beforeRouteEnter (to, from, next) {
        next(vm => { vm.getCurrency() })
    }
}
</script>

<style scoped>
.filter-grid {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 12px;
    align-items: end;
}
.filter-cell { min-width: 0; }
.filter-lbl {
    display: block; font-size: 11px; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.03em;
    color: #6b7280; margin-bottom: 4px; height: 15px;
}
::v-deep .ivu-select-selection { min-height: 34px; }
::v-deep .ivu-input { height: 34px; }

.pill-group {
    display: inline-flex; align-items: center; flex-wrap: nowrap; gap: 3px;
    padding: 3px; background: #f3f4f6; border-radius: 6px;
    height: 34px; box-sizing: border-box; overflow-x: auto; max-width: 100%;
}
.pill {
    background: transparent; border: none; padding: 4px 10px;
    font-size: 12px; color: #4b5563; border-radius: 4px;
    cursor: pointer; transition: all 0.15s; line-height: 1.2;
    white-space: nowrap; display: inline-flex; align-items: center;
}
.pill:hover { background: #e5e7eb; }
.pill-active {
    background: #ffffff; color: #111827; font-weight: 600;
    box-shadow: 0 1px 2px rgba(0,0,0,0.08);
}

.date-row {
    display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.date-picker-wrap {
    flex: 1 1 170px; min-width: 170px;
}
.date-preview {
    font-size: 13px; color: #4b5563; font-weight: 500;
    padding-left: 8px; line-height: 34px; white-space: nowrap;
}

.chip-pill {
    display: inline-flex; align-items: center; padding: 2px 8px;
    font-size: 11px; border-radius: 10px;
    background: #f3f4f6; color: #374151; line-height: 1.5; white-space: nowrap;
}
.chip-default  { background: #f3f4f6; color: #374151; }
.chip-branch   { background: #eef2ff; color: #4338ca; }
.chip-currency { background: #ecfdf5; color: #047857; }
.chip-date     { background: #fef3c7; color: #92400e; }
.chip-type     { background: #dbeafe; color: #1e40af; }
.chip-co       { background: #f3e8ff; color: #6b21a8; }
.chip-search   { background: #fce7f3; color: #9d174d; }

@media (max-width: 991px) {
    .filter-grid > .filter-cell { grid-column: span 12 !important; }
}

.search-group {
    display: flex; align-items: stretch; height: 34px;
    border: 1px solid #d1d5db; border-radius: 6px;
    overflow: hidden; background: #ffffff;
}
.search-input-wrap { position: relative; flex: 1; display: flex; align-items: stretch; min-width: 0; }
.search-input {
    flex: 1; border: none !important; height: 100% !important;
    padding: 0 32px 0 12px; outline: none; background: transparent;
    font-size: 13px; color: #111827; min-width: 0;
}
.search-input:focus { box-shadow: none !important; }
.search-clear {
    position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
    background: transparent; border: none; padding: 4px;
    font-size: 14px; color: #9ca3af; cursor: pointer;
}
.search-btn {
    background: #3b82f6; color: white; border: none;
    padding: 0 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center;
}
.search-btn:hover:not(:disabled) { background: #2563eb; }
.search-btn:disabled { background: #93c5fd; cursor: wait; }
.reset-btn {
    background: #f9fafb; color: #6b7280; border: none;
    border-left: 1px solid #e5e7eb; padding: 0 12px; cursor: pointer;
}
.reset-btn:hover { background: #f3f4f6; color: #374151; }

.results-card {
    background: #ffffff; border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); overflow: hidden;
}
.results-toolbar {
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px 16px; border-bottom: 1px solid #e5e7eb;
    background: #ffffff; gap: 8px; flex-wrap: wrap;
}
.results-body { min-height: 120px; }
.results-pagination {
    display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center;
    padding: 10px 16px; border-top: 1px solid #e5e7eb;
    background: #f9fafb; gap: 8px; min-height: 52px;
}
.pagination-left {
    display: flex; align-items: center; gap: 10px;
    height: 28px; line-height: 28px; font-size: 13px; color: #6b7280;
}
.pagination-lbl { display: inline-flex; align-items: center; height: 28px; line-height: 28px; white-space: nowrap; }
.pagination-lbl b { color: #111827; font-weight: 600; margin: 0 2px; }
.pagination-divider { color: #d1d5db; line-height: 28px; }

.summary-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}
@media (max-width: 991px) { .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 575px) { .summary-grid { grid-template-columns: 1fr; } }
.summary-card {
    display: flex; flex-direction: column; justify-content: space-between; gap: 8px;
    padding: 12px 14px; background: #ffffff;
    border: 1px solid #e5e7eb; border-top: 3px solid;
    border-radius: 6px; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    min-height: 110px;
}
.summary-card__header { display: flex; align-items: center; gap: 8px; }
.summary-card__icon {
    width: 22px; height: 22px; display: flex; align-items: center; justify-content: center;
    border-radius: 4px; font-size: 11px; flex-shrink: 0;
}
.summary-card__label {
    flex: 1; font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.03em; color: #6b7280; line-height: 1.2;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.summary-card__count {
    font-size: 11px; font-weight: 600; color: #9ca3af;
    padding: 1px 6px; background: #f3f4f6; border-radius: 10px; line-height: 1.4;
}
.summary-card__value {
    display: flex; align-items: baseline; gap: 6px;
    line-height: 1; white-space: nowrap; overflow: hidden;
}
.summary-card__amount {
    font-size: 20px; font-weight: 700; color: #111827;
    overflow: hidden; text-overflow: ellipsis; min-width: 0;
}
.summary-card__currency {
    font-size: 11px; font-weight: 600; color: #9ca3af;
    letter-spacing: 0.03em; flex-shrink: 0;
}
.summary-card--cur_due  { border-top-color: #6366f1; }
.summary-card--cur_due  .summary-card__icon { background: #eef2ff; color: #4338ca; }
.summary-card--cur_paid { border-top-color: #10b981; }
.summary-card--cur_paid .summary-card__icon { background: #ecfdf5; color: #047857; }
.summary-card--early    { border-top-color: #3b82f6; }
.summary-card--early    .summary-card__icon { background: #eff6ff; color: #1e40af; }
.summary-card--past_due { border-top-color: #ef4444; }
.summary-card--past_due .summary-card__icon { background: #fee2e2; color: #991b1b; }

::v-deep .ivu-table th {
    background: #f9fafb !important;
    height: 40px; color: #374151; font-weight: 600;
    font-size: 12px; text-transform: uppercase; letter-spacing: 0.02em;
}
::v-deep .ivu-table th .ivu-table-cell {
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
::v-deep .ivu-table-summary td {
    background: #f9fafb !important; font-weight: 700 !important;
    color: #111827 !important; border-top: 2px solid #d1d5db !important;
}
::v-deep .ivu-table td .ivu-table-cell {
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding: 4px 12px;
}
::v-deep .ivu-table td { height: 34px; }
::v-deep .ivu-table-small td { height: 32px; }

.export-group {
    display: inline-flex; align-items: stretch; height: 32px;
    border-radius: 5px; overflow: hidden;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    border: 1px solid #d1d5db; background: #ffffff;
}
.export-select {
    border: 0; border-right: 1px solid #d1d5db;
    background: #ffffff; padding: 0 28px 0 12px;
    font-size: 12px; color: #374151; font-weight: 500;
    cursor: pointer; outline: none; height: 100%;
    appearance: none; -webkit-appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3e%3cpath fill='%236b7280' d='M5 6L0 0h10z'/%3e%3c/svg%3e");
    background-repeat: no-repeat; background-position: right 10px center; background-size: 10px 6px;
}
.export-btn {
    background: #10b981; color: white; border: none;
    padding: 0 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center;
}
.export-btn:hover:not(:disabled) { background: #059669; }
.export-btn:disabled { background: #e5e7eb; color: #9ca3af; cursor: not-allowed; }

.empty-state {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 40px 20px; text-align: center; height: 100%; position: absolute; inset: 0;
}
</style>

<!-- Unscoped: render-function elements don't receive data-v-xxx. -->
<style>
.name-text {
    color: #111827; font-size: 12px; font-weight: 500;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.branch-code {
    display: inline-block; padding: 1px 6px;
    font-size: 11px; font-weight: 600;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    color: #4338ca; background: #eef2ff;
    border: 1px solid #e0e7ff; border-radius: 3px;
    letter-spacing: 0.02em;
}

/* Status cell pills — Active (green) vs Inactive (red). */
.status-pill {
    display: inline-block; padding: 1px 8px;
    font-size: 11px; font-weight: 600;
    border-radius: 10px; letter-spacing: 0.02em;
}
.status-active   { color: #047857; background: #ecfdf5; border: 1px solid #a7f3d0; }
.status-inactive { color: #991b1b; background: #fee2e2; border: 1px solid #fecaca; }
</style>
